import { AnalysisResult, AnalysisData, ProcessingProgress, ProcessingStep } from '../types';
import { PROCESSING_STEPS } from '../utils/constants';

// Generate a unique ID
const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// Mock map image (simple SVG as base64)
const MOCK_MAP_IMAGE = `data:image/svg+xml;base64,${btoa(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#e8f5e9"/>
      <stop offset="100%" style="stop-color:#c8e6c9"/>
    </linearGradient>
  </defs>
  <rect fill="url(#bg)" width="400" height="300"/>
  <path d="M50,100 L150,80 L200,120 L180,180 L80,160 Z" fill="#4caf50" stroke="#2e7d32" stroke-width="2"/>
  <path d="M200,120 L320,100 L350,160 L280,200 L180,180 Z" fill="#66bb6a" stroke="#388e3c" stroke-width="2"/>
  <path d="M80,160 L180,180 L160,250 L60,230 Z" fill="#81c784" stroke="#43a047" stroke-width="2"/>
  <circle cx="180" cy="150" r="4" fill="#1976d2"/>
  <text x="185" y="145" font-size="10" fill="#1976d2">Center</text>
  <!-- Scale bar -->
  <line x1="300" y1="270" x2="380" y2="270" stroke="#333" stroke-width="2"/>
  <text x="320" y="285" font-size="10" fill="#333">100m</text>
  <!-- North arrow -->
  <polygon points="30,30 25,50 30,45 35,50" fill="#333"/>
  <text x="26" y="60" font-size="10" fill="#333">N</text>
</svg>
`)}`;

// Mock analysis data
export const mockAnalysisData: AnalysisData = {
    summary: {
        totalArea: 15234.56,
        totalAreaHectares: 1.52,
        perimeter: 523.45,
        parcelCount: 3,
        centerPoint: {
            lat: -6.2088,
            lng: 106.8456,
        },
        boundingBox: {
            north: -6.205,
            south: -6.212,
            east: 106.85,
            west: 106.84,
        },
        coordinateSystem: 'WGS 84 / EPSG:4326',
    },
    parcels: [
        {
            id: 'parcel-001',
            name: 'Feature A',
            area: 5234.56,
            perimeter: 234.56,
            coordinates: [
                { lat: -6.206, lng: 106.842 },
                { lat: -6.206, lng: 106.848 },
                { lat: -6.21, lng: 106.848 },
                { lat: -6.21, lng: 106.842 },
            ],
            properties: {
                owner: 'PT. Contoh',
                zoning: 'Residential',
            },
        },
        {
            id: 'parcel-002',
            name: 'Feature B',
            area: 6500.0,
            perimeter: 180.22,
            coordinates: [
                { lat: -6.206, lng: 106.848 },
                { lat: -6.208, lng: 106.852 },
                { lat: -6.212, lng: 106.85 },
                { lat: -6.21, lng: 106.848 },
            ],
            properties: {
                owner: 'CV. Maju',
                zoning: 'Commercial',
            },
        },
        {
            id: 'parcel-003',
            name: 'Feature C',
            area: 3500.0,
            perimeter: 108.67,
            coordinates: [
                { lat: -6.21, lng: 106.842 },
                { lat: -6.21, lng: 106.848 },
                { lat: -6.215, lng: 106.846 },
                { lat: -6.214, lng: 106.841 },
            ],
            properties: {
                owner: 'Pribadi',
                zoning: 'Residential',
            },
        },
    ],
    interpretation: {
        summary:
            'Analysis shows 3 spatial features with total area of 1.52 hectares. Location is in the South Jakarta area with mixed residential and commercial zoning.',
        details: [
            {
                category: 'Location',
                title: 'Geographic Position',
                description:
                    'Spatial data located at coordinates -6.2088, 106.8456 in the South Jakarta area.',
                severity: 'info',
            },
            {
                category: 'Size',
                title: 'Total Area',
                description:
                    'Total area of all three features is 15,234.56 m² or equivalent to 1.52 hectares.',
                severity: 'info',
            },
            {
                category: 'Geometry',
                title: 'Feature Shape',
                description:
                    'All three features have regular polygon shapes with a total perimeter of 523.45 meters.',
                severity: 'info',
            },
            {
                category: 'Zoning',
                title: 'Land Use',
                description:
                    'There are 2 residential zones and 1 commercial zone.',
                severity: 'warning',
            },
        ],
        recommendations: [
            'Verify boundaries with field survey to ensure data accuracy',
            'Check ownership status with local land registry office',
            'Confirm zoning and land use with local city planning department',
            'Ensure no overlap with surrounding spatial features',
        ],
    },
    mapImage: MOCK_MAP_IMAGE,
};

// Create a mock analysis result
export const createMockAnalysisResult = (fileId: string): AnalysisResult => ({
    id: generateId(),
    fileId,
    status: 'complete',
    createdAt: new Date(),
    completedAt: new Date(),
    data: mockAnalysisData,
});

// Create initial processing steps
export const createProcessingSteps = (): ProcessingStep[] => {
    return PROCESSING_STEPS.map((step) => ({
        id: step.id,
        name: step.name,
        description: step.description,
        status: 'pending' as const,
        progress: 0,
    }));
};

// Create mock processing progress
export const createMockProcessingProgress = (
    analysisId: string,
    currentStep: number,
    steps: ProcessingStep[]
): ProcessingProgress => {
    const totalDuration = PROCESSING_STEPS.reduce((sum, s) => sum + s.duration, 0);
    const completedDuration = PROCESSING_STEPS.slice(0, currentStep).reduce(
        (sum, s) => sum + s.duration,
        0
    );
    const overallProgress = Math.round((completedDuration / totalDuration) * 100);
    const remainingDuration = (totalDuration - completedDuration) / 1000;

    return {
        analysisId,
        currentStep,
        totalSteps: steps.length,
        overallProgress,
        estimatedTimeRemaining: remainingDuration,
        steps,
    };
};

// Simulate random variation in mock data
export const randomizeAnalysisData = (baseData: AnalysisData): AnalysisData => {
    const variation = 0.9 + Math.random() * 0.2; // 90% to 110%
    return {
        ...baseData,
        summary: {
            ...baseData.summary,
            totalArea: Math.round(baseData.summary.totalArea * variation * 100) / 100,
            totalAreaHectares:
                Math.round(baseData.summary.totalAreaHectares * variation * 100) / 100,
            perimeter: Math.round(baseData.summary.perimeter * variation * 100) / 100,
        },
    };
};
